import React from "react";
import BlogOne from "../assets/Banner/Blogs/BlogOne.png";
import Header from "../layout/Header";

interface SectionTitleProps {
  children: React.ReactNode;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ children }) => (
  <h3
    className="
      font-poppins 
      mb-2 
      text-[#000]
      font-normal
      text-xl
      md:text-[2.12rem] 
      lg:text-[2.12rem] 
      lg:font-medium 
      md:font-medium
      font-semibold
    "
    style={{
      fontWeight: window.innerWidth >= 768 ? 500 : undefined,
      fontSize: window.innerWidth >= 768 ? "2.12rem" : undefined,
    }}
  >
    {children}
  </h3>
);

interface SectionDescriptionProps {
  children: React.ReactNode;
}

const SectionDescription: React.FC<SectionDescriptionProps> = ({
  children,
}) => (
  <p
    className="text-[#000] mb-2 text-justify text-basee"
    style={{
      fontWeight: 400,
      fontSize: window.innerWidth >= 1024 ? "1.3rem" : undefined,
    }}
  >
    {children}
  </p>
);

interface SectionListProps {
  items: string[];
}

const SectionList: React.FC<SectionListProps> = ({ items }) => (
  <ul className="pl-6 mb-2 list-disc">
    {items.map((item, idx) => (
      <li key={idx}>
        <p className="font-poppins text-[#222] mb-1 text-justify text-base">
          {item}
        </p>
      </li>
    ))}
  </ul>
);

const BlogDetails: React.FC = () => {
  return (
    <>
      {/* Header always at the very top */}
      <div className="w-full fixed top-0 left-0 z-20">
        <Header />
      </div>
      {/* Banner image directly below header, always visible on all screens */}
      <div className="w-full pt-[75px] relative">
        <img
          src={BlogOne}
          alt="About Us Banner"
          className="w-full h-64 md:h-100 lg:h-[89vh] object-cover"
        />
      </div>

      <div className="flex-1 bg-white min-h-screen alignment-center pt-10">
        {/* Container for md and up, px-4 for small screens */}
        <div className="px-6  md:p-[0 1rem] md:mx-auto max-w-screen-xl">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Main Blog Content */}
            <div className="w-full md:w-[99%]">
              {/* Section 1 */}
              <div className="mb-8">
                <SectionTitle>
                  Understanding the Assistance Landscape
                </SectionTitle>
                <SectionDescription>
                  Before delving into the core of customer-centricity, it's
                  crucial to understand the challenges that businesses face in
                  the realm of assistance services. In an interconnected global
                  environment, businesses encounter a myriad of unforeseen
                  circumstances - from travel disruptions and medical
                  emergencies to legal intricacies. Navigating this complex
                  landscape requires a partner that not only delivers services
                  but does so with a deep understanding of the client's specific
                  requirements.
                </SectionDescription>
                {/* Well styling */}
                <div className="bg-[#f8fbff] border-l-4 border-[#1976d2] px-4 py-3 mb-2 rounded-md shadow-sm">
                  <p className="italic font-poppins font-normal text-justify text-[0.98rem] sm:text-base md:text-[1.05rem]">
                    “In a world where customer expectations are evolving, being
                    customer-centric is not just a choice; it's a necessity.
                    Customer-centricity is not a strategy; it's a culture. It's
                    about more than delivering services; it's about creating
                    value, building lasting relationships, and being a reliable
                    partner in clients' success.”
                  </p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-8">
                <SectionTitle>
                  The Significance of Customer-Centricity in Assistance Services
                </SectionTitle>
                <SectionDescription>
                  <b>Guided Assistance/Supportive Handholding</b>
                  <br />
                  Unlike traditional insurance or reimbursement models, the
                  approach of “handholding” goes beyond the conventional. It
                  involves offering unparalleled support, providing a guiding
                  presence, and acting as a constant companion in times of
                  uncertainty. The focus is not just on delivering services but
                  on walking the journey with clients, ensuring they are never
                  alone in navigating challenges.
                </SectionDescription>
                <SectionDescription>
                  <b>Tailoring Solutions to Unique Needs</b>
                  <br />A one-size-fits-all approach doesn't cut it in the
                  diverse assistance landscape. Customer-centricity means
                  delving into the unique requirements of each client and
                  tailoring assistance services accordingly.
                </SectionDescription>
                <SectionDescription>
                  <b>Seamless Experience Across Services</b>
                  <br />
                  Businesses often require a spectrum of services – from travel
                  assistance and legal support to medical aid. Being
                  customer-centric means ensuring a seamless experience across
                  these services, providing a unified and harmonious service
                  journey.
                </SectionDescription>
                <SectionDescription>
                  <b>Anticipation of Needs</b>
                  <br />
                  True customer-centricity involves anticipating future
                  requirements and proactively providing solutions. The focus
                  should be on preventing issues, ensuring that clients have the
                  support they need before they even realize it.
                </SectionDescription>
              </div>

              {/* Section 3 */}
              <div className="mb-8">
                <SectionTitle>
                  The Customer-Centric Approach in Assistance Services
                </SectionTitle>
                <SectionList
                  items={[
                    "Personalized Assistance Plans: The journey starts with crafting personalized assistance plans aligned with the unique demands of each client, ensuring services are not just effective but also efficient.",
                    "24/7 Accessibility: Emergencies don't wait for office hours. Commitment to 24/7 accessibility ensures that assistance is just a call away, providing a sense of security and reliability.",
                  ]}
                />
              </div>

              {/* Section 4 */}
              <div className="mb-8">
                <SectionTitle>VAS in Insurance</SectionTitle>
                <SectionDescription>
                  <b>Proactive Communication:</b> Open and proactive
                  communication keeps clients informed at every step, building
                  trust, a cornerstone of client relationships.
                </SectionDescription>
                <SectionDescription>
                  <b>Continuous Innovation:</b> A customer-centric approach
                  involves continuous improvement and technological innovation,
                  adapting to the evolving needs of clients.
                </SectionDescription>
              </div>

              {/* Conclusion */}
              <div className="mb-8">
                <SectionTitle>
                  Conclusion: Redefining Assistance Services
                </SectionTitle>
                <SectionDescription>
                  In a world where customer expectations are evolving, being
                  customer-centric is not just a choice; it's a necessity.
                  Customer-centricity is not a strategy; it's a culture. It's
                  about more than delivering services; it's about creating
                  value, building lasting relationships, and being a reliable
                  partner in clients' success. As the industry navigates the
                  intricate landscape of assistance services,
                  customer-centricity remains the guiding North Star. It's what
                  sets companies apart, positions them as thought leaders, and
                  ensures that clients receive an experience that exceeds
                  expectations. Explore a culture of customer-centricity, where
                  it's not just a commitment but a way of redefining assistance
                  services.
                </SectionDescription>
              </div>
            </div>

            {/* <div className="w-full md:w-[30%] flex flex-col gap-6">
           
              <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
                <h3 className="font-poppins text-[1.91rem] font-semibold text-lg mb-3 text-[#1976d2]">
                  Recent Posts
                </h3>
                <ul className="space-y-2">
                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                        <img
                          src={BlogOne}
                          alt="How Customer-Centricity Drives Success"
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div>
                        <a
                          href="#"
                          className="block text-[#222] hover:text-[#1976d2] font-medium transition"
                        >
                          How Customer-Centricity Drives Success
                        </a>
                        <span className="block text-xs text-gray-400">
                          June 2024
                        </span>
                      </div>
                    </div>
                  </li>
                  <hr />

                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden bg-gray-100" />
                      <div>
                        <a
                          href="#"
                          className="block text-[#222] hover:text-[#1976d2] font-medium transition"
                        >
                          5 Ways to Improve Assistance Services
                        </a>
                        <span className="block text-xs text-gray-400">
                          May 2024
                        </span>
                      </div>
                    </div>
                  </li>
                  <hr />
                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden bg-gray-100" />
                      <div>
                        <a
                          href="#"
                          className="block text-[#222] hover:text-[#1976d2] font-medium transition"
                        >
                          The Future of VAS in Insurance
                        </a>
                        <span className="block text-xs text-gray-400">
                          April 2024
                        </span>
                      </div>
                    </div>
                  </li>
                  <hr />    
                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                        <img
                          src={BlogOne}
                          alt="How Customer-Centricity Drives Success"
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div>
                        <a
                          href="#"
                          className="block text-[#222] hover:text-[#1976d2] font-medium transition"
                        >
                          How Customer-Centricity Drives Success
                        </a>
                        <span className="block text-xs text-gray-400">
                          June 2024
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-xl shadow border border-gray-100 p-5 mb-6">
                <h3 className="font-poppins text-[1.91rem] font-semibold text-lg mb-3 text-[#1976d2]">
                  Facebook Post
                </h3>
                <ul className="space-y-2">
                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                        <img
                          src={BlogOne}
                          alt="Facebook Post"
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div>
                        <a
                          href="#"
                          className="block text-[#222] hover:text-[#1976d2] font-medium transition"
                        >
                          Customer Experience Trends 2024
                        </a>
                        <span className="block text-xs text-gray-400">
                          Posted on Facebook
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl shadow border border-gray-100 p-5">
                <h3 className="font-poppins text-[1.91rem] font-semibold text-lg mb-3 text-[#E1306C]">
                  Instagram Post
                </h3>
                <ul className="space-y-2">
                  <li>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 flex-shrink-0 rounded overflow-hidden bg-gray-100">
                        <img
                          src={BlogOne}
                          alt="Instagram Post"
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div>
                        <a
                          href="#"
                          className="block  text-[#222] hover:text-[#E1306C] font-medium transition"
                        >
                          Customer Experience Trends 2024
                        </a>
                        <span className="block text-xs text-gray-400">
                          Posted on Instagram
                        </span>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;
